import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppSnapshot } from '../../app/state';
import { callAction } from '../../app/actions';
import * as Todos from '../../api/todos';
import type { MyTodo, Todo } from '../../api/todos';
import { SubjectPicker, initialsOf } from '../../components/SubjectPicker';
import { DatePicker } from '../../components/DatePicker';
import { canEditTodos, canEnterEdit, canViewTodos } from '../../lib/permissions';
import {
  dayKeyOf,
  displayTree,
  formatDue as formatDueOn,
  hasNotes,
  isNoopDrop,
  isOverdue as isOverdueOn,
  moveInTree,
  rollupOf,
  type DropTarget,
  type DropWhere,
} from '../../lib/todos';
import { todoText, todoTextf } from '../../i18n/todos';
import type { MapNode } from '../../lib/types';
import { CountPill, PopupFold } from './PopupFold';
import { SECTION_TODOS } from './sections';
import { TaskDetail, TaskRow, type RowContext } from './TaskRow';

/**
 * THE NODE POPUP'S TASK SECTION (V0.50; reshaped V0.51.1).
 *
 * ── WHAT CHANGED, AND WHY ────────────────────────────────────────────────────────────────
 *
 * A task can hold tasks now, and sub-tasks occupy the space under a row. That space was where
 * the notes lived, so the two could not both have it: the row had to learn to say more in
 * itself, and the rest of what a task holds needed a home that was not "below". The user chose
 * the shape in a proof of concept — two-line rows, a detail CARD opened by one click with the
 * sub-tasks following beneath it, the author as a pill on every row, elbow connectors, a 40px
 * indent — and what follows is that arrangement rather than a set of options.
 *
 * ── THE THREE RULES THIS FILE OWNS ───────────────────────────────────────────────────────
 *
 * 1. ONE CLICK OPENS THE CARD, TWO OPEN THE EDITOR. The single click waits 220ms so the first
 *    half of a double-click does not flash the card open and shut. Keyboard Enter does not
 *    wait, because there is no second gesture it could turn out to be.
 *
 * 2. THE DRAG SHOWS WHERE IT WILL LAND, AND ONLY WHEN IT WILL MOVE. V0.50 had ONE drop verb —
 *    "before this row" — so there was no target after the last row and a task could not be sent
 *    to the bottom at all, which is what the user reported. Worse, the whole hovered row lit up
 *    whatever half the cursor was in, including the rows either side of the dragged one, where
 *    letting go did nothing. Now the cursor's half picks before or after, its middle picks
 *    "into" on a root, a zone after the last row of every list is a real target, and a drop
 *    that would change nothing draws nothing (`isNoopDrop`).
 *
 * 3. WHAT IS SENT IS WHAT IS DRAWN. A drop rebuilds the whole arrangement from the tree on
 *    screen (`moveInTree` then `flattenArrangement`) and sends it as one permutation. The old
 *    code derived an order from a filtered display list while `position` spanned a different
 *    set of rows — the second half of why dragging misbehaved.
 *
 * ── UNCHANGED, AND WORTH NOT UNDOING ─────────────────────────────────────────────────────
 *
 * The fold is `PopupFold`'s: SHUT by default, and what this user opens stays open on the next
 * node. It fetches on open — a list whose contents are the point cannot be lazily loaded behind
 * an interaction, and the folded summary's count needs the data too — and re-reads whenever
 * `s.todosVersion` moves, so a row ticked in the "My items" pane updates here without either
 * surface knowing the other exists.
 *
 * READ-ONLY IS NOT CHECKBOX-ONLY. In View-only mode, and for anybody without `EDIT_TODOS`, the
 * list is read-only EXCEPT the caller's own checkboxes — the server's rule (`mayTick`) drawn
 * rather than a second policy — and a checkbox the server would refuse is DISABLED, never
 * hidden: "you cannot tick this" and "this has no checkbox" are different sentences.
 *
 * `mayEdit`'s carve-out stands: View-only suppresses the editing controls only for somebody who
 * HAS the Edit toggle. A task manager with no map bricks never sees that toggle, and gating a
 * granted permission behind one that is never drawn is how a permission becomes decoration.
 */

/**
 * Today, as `YYYY-MM-DD` in LOCAL time — the calendar the reader is actually looking at.
 *
 * The FORMULA is `lib/todos.ts`'s `dayKeyOf`; the clock read stays HERE, because nothing under
 * `lib/` may read it (`lib/health.ts`'s rule, which is what lets the Projects tab's tests ask
 * about any day). Exported for that tab, and now for the date picker's five call sites.
 */
export const todayKey = (): string => dayKeyOf(new Date());

/**
 * The due-date predicates, BOUND to today (V0.50.2).
 *
 * `lib/todos.ts` holds the one implementation of each with `today` as a parameter — string
 * comparison, never `Date` — and these wrappers keep the shape this file's callers always had.
 */
const isOverdue = (due: string | null, state: string): boolean => isOverdueOn(due, state, todayKey());
const formatDue = (due: string): string => formatDueOn(due, todayKey());

/** How long a single click waits to see whether a second one is coming. */
const DOUBLE_CLICK_GRACE = 220;

export function TodoSection({
  node,
  s,
  readOnly,
}: {
  node: MapNode;
  s: AppSnapshot;
  /** The popup's own read shape, or View-only mode. Checkboxes survive it; nothing else does. */
  readOnly: boolean;
}) {
  const [todos, setTodos] = useState<Todo[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState<ReadonlySet<string>>(() => new Set());
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [composer, setComposer] = useState<{
    parentId: string | null;
    indentUnder: string | null;
    text: string;
  }>({ parentId: null, indentUnder: null, text: '' });
  const [drag, setDrag] = useState<{ id: string; over: DropTarget | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusAdd = useRef(false);
  const addRef = useRef<HTMLInputElement>(null);

  const mayView = canViewTodos(s.perm);
  const mayEdit = canEditTodos(s.perm) && (!readOnly || !canEnterEdit(s.perm));

  /* The one fetch. Guarded so a popup closed mid-flight does not set state on an unmounted
     body, and re-run when any task anywhere changes (see the header's refresh paths). */
  useEffect(() => {
    if (!mayView) return;
    let live = true;
    Todos.fetchNodeTodos(node.id)
      .then((r) => {
        if (!live) return;
        setTodos(r.todos);
        // The mark rides the map payload (V0.50.3); this fetch carries it too, so a colleague's
        // marking since the last map load reaches the head without a reload.
        if ('project' in r) callAction('adoptNodeProject', node.id, r.project ?? null);
      })
      .catch(() => {
        // A 404 here means the node went away or is no longer visible; either way the popup is
        // about to be wrong for bigger reasons. An empty list is the honest local answer.
        if (!live) return;
        setTodos([]);
      });
    return () => {
      live = false;
    };
  }, [node.id, mayView, s.todosVersion]);

  /**
   * "Open THIS one" — the instruction a click in the "My items" pane leaves behind (V0.50.1).
   *
   * Opens the task's CARD now rather than its notes, and unfolds its parent when it is a
   * sub-task: a row hidden inside a folded parent is not shown by being selected.
   */
  useEffect(() => {
    if (!s.focusTodoId || !todos) return;
    const target = todos.find((t) => t.id === s.focusTodoId);
    if (target) {
      setDetailOpen(new Set([target.id]));
      const parentId = target.parentId;
      if (parentId) {
        setFolded((prev) => {
          if (!prev.has(parentId)) return prev;
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
      callAction('setSectionOpen', SECTION_TODOS, true);
    }
    callAction('consumeFocusTodo');
  }, [s.focusTodoId, todos]);

  /**
   * "Add a task…" from the right-click menu (V0.54): the new-task box, focused, once — handed
   * over as `focusTodoAdd` for THIS node and consumed here, the instruction above's shape. Waits
   * for the list, because the box is drawn under it.
   */
  useEffect(() => {
    if (s.focusTodoAdd !== node.id || !todos) return;
    if (mayEdit) addRef.current?.focus();
    callAction('consumeFocusTodoAdd');
  }, [s.focusTodoAdd, todos, node.id, mayEdit]);

  /* Keep the composer focused across the re-render its own Enter causes, so a list can be
     typed straight down without reaching for the mouse between items. */
  useEffect(() => {
    if (!focusAdd.current) return;
    focusAdd.current = false;
    addRef.current?.focus();
  });

  const refresh = useCallback(() => callAction('notifyTodosChanged'), []);

  const run = useCallback(
    async (id: string, fn: () => Promise<unknown>) => {
      setBusy(id);
      setError(null);
      try {
        await fn();
        refresh();
      } catch (e) {
        // The 403 path is real: the server refuses a tick on somebody else's row, and a
        // silently-reverted checkbox would read as the app losing the click.
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(null);
      }
    },
    [refresh],
  );

  if (!mayView) return null; // Rule: no permission, no section at all — not an empty one.

  const list = todos ?? [];
  const tree = displayTree(list);
  const locale = s.currentLocale;
  const ctx: RowContext = { s, today: todayKey(), all: list };

  /** Every row, parent included, counts once — the figure the Projects tab reports too. */
  const doneCount = list.filter((t) => t.state === 'done').length;

  /** May THIS reader tick THIS row? The server's rule, drawn. */
  const mayTick = (t: Todo): boolean => {
    if (canEditTodos(s.perm)) return true;
    if (s.currentUser && t.assigneeUserIds.includes(s.currentUser.id)) return true;
    return !!s.currentUser?.groupId && t.assigneeGroupIds.includes(s.currentUser.groupId);
  };

  const toggleDetail = (id: string) =>
    setDetailOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const unfold = (id: string) =>
    setFolded((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const toggleFold = (id: string) =>
    setFolded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  /** One click, after a beat; a double-click cancels it on the way past. */
  const openCard = (id: string) => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      toggleDetail(id);
    }, DOUBLE_CLICK_GRACE);
  };

  const openEditor = (id: string) => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    if (!mayEdit) return;
    setConfirmId(null);
    setEditingId(id);
  };

  const add = async (title: string, parentId: string | null) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setComposer((c) => ({ ...c, text: '' }));
    focusAdd.current = true;
    if (parentId) unfold(parentId);
    await run('new', () =>
      Todos.createTodo(node.id, parentId ? { title: trimmed, parentId } : { title: trimmed }),
    );
  };

  const remove = async (t: Todo) => {
    // A cascade is confirmed first, and the confirm names the count — "delete this" and "delete
    // these four things" are different decisions, and the row cannot say which one it is.
    if (rollupOf(list, t.id).total > 0) {
      setConfirmId(t.id);
      return;
    }
    await run(t.id, () => Todos.deleteTodo(t.id));
  };

  /* ------------------------------------------------------------------------------ drag */

  /** Where would a drop on this row land? null when it would change nothing, or is refused. */
  const whereOn = (e: React.DragEvent, target: Todo): DropWhere | null => {
    if (!drag) return null;
    const dragged = list.find((t) => t.id === drag.id);
    if (!dragged) return null;
    const r = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    const nestable =
      !target.parentId &&
      target.id !== dragged.id &&
      rollupOf(list, dragged.id).total === 0 &&
      dragged.parentId !== target.id;
    const where: DropWhere = nestable && y > 0.35 && y < 0.65 ? 'into' : y < 0.5 ? 'before' : 'after';
    if (isNoopDrop(list, dragged.id, target.id, where)) return null;
    // A move the one-level rule forbids is never offered — the server refuses it too.
    return moveInTree(list, dragged.id, { id: target.id, where }) ? where : null;
  };

  const sameTarget = (a: DropTarget | null, b: DropTarget | null) =>
    JSON.stringify(a) === JSON.stringify(b);

  const setOver = (over: DropTarget | null) =>
    setDrag((d) => (d && !sameTarget(d.over, over) ? { ...d, over } : d));

  const commitDrop = async () => {
    const current = drag;
    setDrag(null);
    if (!current?.over) return;
    const next = moveInTree(list, current.id, current.over);
    if (!next) return;
    await run(current.id, () => Todos.reorderTodos(node.id, next.ids, next.parents));
  };

  /**
   * The drop target at the end of a list — the one V0.50 had no way to express.
   *
   * A PLAIN FUNCTION returning markup, not a component declared in the render body: a component
   * declared here has a new identity on every render, so React unmounts and remounts its DOM —
   * and a drop target torn down mid-drag is one the browser stops sending events to. Same
   * reason the composer below is a function.
   */
  const dropZone = (parentId: string | null) => {
    if (!drag) return null;
    if (!moveInTree(list, drag.id, { endOf: parentId })) return null;
    const hot = !!drag.over && 'endOf' in drag.over && drag.over.endOf === parentId;
    return (
      <div
        className={`todo-dropzone${hot ? ' hot' : ''}`}
        data-zone={parentId ?? ''}
        onDragOver={(e) => {
          e.preventDefault();
          setOver({ endOf: parentId });
        }}
        onDrop={(e) => {
          e.preventDefault();
          void commitDrop();
        }}
      />
    );
  };

  /** The gap that opens where the row will land. A function, for `dropZone`'s reason. */
  const gap = (id: string, where: 'before' | 'after') =>
    drag?.over && 'id' in drag.over && drag.over.id === id && drag.over.where === where ? (
      <div className="todo-gap" aria-hidden="true" />
    ) : null;

  /* -------------------------------------------------------------------------- keyboard */

  /** Up and down between visible rows, Space ticks, Enter edits (or opens the card). */
  const onKeyNav = (e: React.KeyboardEvent<HTMLDivElement>, t: Todo) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const host = e.currentTarget.closest('.todolist') as HTMLElement | null;
      const rows = Array.from(host?.querySelectorAll<HTMLElement>('.todorow') ?? []);
      const i = rows.indexOf(e.currentTarget);
      rows[i + (e.key === 'ArrowDown' ? 1 : -1)]?.focus();
    } else if (e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (mayTick(t) && busy !== t.id) {
        void run(t.id, () => Todos.setTodoState(t.id, t.state === 'done' ? 'open' : 'done'));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (mayEdit) openEditor(t.id);
      else toggleDetail(t.id);
    }
  };

  /* ----------------------------------------------------------------------- the composer */

  const composerParent = composer.parentId ?? composer.indentUnder;
  const composerHost = list.find((t) => t.id === composerParent) ?? null;

  const composerBox = (inline: boolean) => (
    <input
      ref={addRef}
      id="pp-todo-add"
      className={`todo-add${inline ? ' child' : composer.indentUnder ? ' indent' : ''}`}
      data-parent={composerParent ?? ''}
      value={composer.text}
      placeholder={
        composerHost
          ? todoTextf('addChildPlaceholder', locale, { s: composerHost.title })
          : todoText('addPlaceholder', locale)
      }
      autoComplete="off"
      onChange={(e) => setComposer((c) => ({ ...c, text: e.target.value }))}
      /* Enter commits, Escape abandons, Tab indents. `stopPropagation` throughout: the popup
         and chrome.ts both listen for these keys, and writing a task must not also save the
         node — nor abandoning one close the whole popup. */
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          void add(composer.text, composerParent);
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          if (composerParent) setComposer({ parentId: null, indentUnder: null, text: composer.text });
          else if (composer.text) setComposer({ parentId: null, indentUnder: null, text: '' });
        } else if (e.key === 'Tab' && !composer.parentId) {
          /* TAB INDENTS, Shift+Tab outdents — the outline gesture, aimed at the last open root
             because that is the task somebody has just been writing under. Only in the standing
             composer: one opened by the `+` already knows its parent. */
          const lastRoot = tree.filter((b) => b.task.state !== 'done').at(-1) ?? tree.at(-1);
          if (!e.shiftKey && !lastRoot) return;
          e.preventDefault();
          setComposer((c) => ({ ...c, indentUnder: e.shiftKey ? null : (lastRoot?.task.id ?? null) }));
          focusAdd.current = true;
        }
      }}
    />
  );

  /* -------------------------------------------------------------------------- rendering */

  const childComposer = (parentId: string) =>
    mayEdit && composer.parentId === parentId ? (
      <div className="todoblock composer-child">{composerBox(true)}</div>
    ) : null;

  const renderRow = (t: Todo, depth: 0 | 1) => {
    if (editingId === t.id) {
      return (
        <TaskEditor
          s={s}
          todo={t}
          onCancel={() => setEditingId(null)}
          onSave={async (draft) => {
            setEditingId(null);
            await run(t.id, () => Todos.updateTodo(t.id, draft));
          }}
        />
      );
    }
    if (confirmId === t.id) {
      const n = rollupOf(list, t.id).total;
      return (
        <div className="todo-confirm">
          <span>
            {n === 1 ? todoText('confirmDeleteParent1', locale) : todoTextf('confirmDeleteParent', locale, { n })}
          </span>
          <button
            type="button"
            className="pbtn danger"
            onClick={() => {
              setConfirmId(null);
              void run(t.id, () => Todos.deleteTodo(t.id));
            }}
          >
            {todoText('deleteVerb', locale)}
          </button>
          <button type="button" className="pbtn" onClick={() => setConfirmId(null)}>
            {todoText('cancel', locale)}
          </button>
        </div>
      );
    }
    return (
      <TaskRow
        ctx={ctx}
        todo={t}
        depth={depth}
        busy={busy === t.id}
        canTick={mayTick(t)}
        canEdit={mayEdit}
        open={detailOpen.has(t.id)}
        folded={depth === 0 && rollupOf(list, t.id).total > 0 ? folded.has(t.id) : undefined}
        dragging={drag?.id === t.id}
        dropInto={!!drag?.over && 'id' in drag.over && drag.over.id === t.id && drag.over.where === 'into'}
        onOpen={() => openCard(t.id)}
        onEditor={() => openEditor(t.id)}
        onToggle={() => void run(t.id, () => Todos.setTodoState(t.id, t.state === 'done' ? 'open' : 'done'))}
        onDelete={() => void remove(t)}
        onFold={() => toggleFold(t.id)}
        onAddSub={() => {
          setComposer({ parentId: t.id, indentUnder: null, text: '' });
          focusAdd.current = true;
          unfold(t.id);
        }}
        onKeyNav={(e) => onKeyNav(e, t)}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/x-todo', t.id);
          e.dataTransfer.effectAllowed = 'move';
          setDrag({ id: t.id, over: null });
        }}
        onDragEnd={() => setDrag(null)}
        onDragOver={(e) => {
          if (!drag || t.state === 'done') return;
          e.preventDefault();
          const where = whereOn(e, t);
          setOver(where ? { id: t.id, where } : null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          void commitDrop();
        }}
      />
    );
  };

  const detailFor = (t: Todo) =>
    detailOpen.has(t.id) && editingId !== t.id && confirmId !== t.id ? (
      <TaskDetail ctx={ctx} todo={t} canEdit={mayEdit} onEditor={() => openEditor(t.id)} />
    ) : null;

  return (
    <PopupFold
      sectionKey={SECTION_TODOS}
      id="pp-todos"
      summaryId="pp-todos-summary"
      className="todopanel"
      bodyClassName="todolist"
      label={todoText('section', locale)}
      /* The count reads folded or not — it is the one fact somebody opens this for, and the
         reason `count` is drawn in both states while every other section's value swaps. */
      count={
        list.length ? (
          <CountPill>
            {doneCount}/{list.length}
          </CountPill>
        ) : undefined
      }
    >
      <>
        {error ? <div className="todo-error">{error}</div> : null}

        {tree.map(({ task, children }) => {
          const showKids = children.length > 0 && !folded.has(task.id);
          return (
            <div className="todoblock" data-block={task.id} key={task.id}>
              {gap(task.id, 'before')}
              {renderRow(task, 0)}
              {detailFor(task)}
              {showKids ? (
                <div className="todochildren">
                  {children.map((kid) => (
                    <div className="todoblock" data-block={kid.id} key={kid.id}>
                      {gap(kid.id, 'before')}
                      {renderRow(kid, 1)}
                      {detailFor(kid)}
                      {gap(kid.id, 'after')}
                    </div>
                  ))}
                  {dropZone(task.id)}
                  {childComposer(task.id)}
                </div>
              ) : null}
              {/* A parent with its sub-tasks folded away, or none yet, still takes a new one. */}
              {showKids ? null : childComposer(task.id)}
              {gap(task.id, 'after')}
            </div>
          );
        })}

        {dropZone(null)}

        {!list.length && todos !== null ? (
          <div className="todo-empty">{todoText(mayEdit ? 'empty' : 'emptyReadOnly', locale)}</div>
        ) : null}

        {mayEdit && !composer.parentId ? composerBox(false) : null}

        {list.length || mayEdit ? (
          <div className="todo-hint">{todoText(mayEdit ? 'hintEdit' : 'hintRead', locale)}</div>
        ) : null}
      </>
    </PopupFold>
  );
}

/* ------------------------------------------------------------------------- the editor */

/**
 * One task, open for editing.
 *
 * THE SAME `SubjectPicker` the responsibles row uses — the one implementation, per the house
 * rule — with NO constraint on it: being given a task is not a role anybody has to be permitted
 * to hold, and filtering to `NODE_RESPONSIBLE` would mean a team could not put a job on a
 * colleague's list until an administrator had ticked a box for them. The server agrees;
 * `badAssignee()` checks existence and nothing else.
 *
 * "Assign to me" sits BESIDE the picker rather than inside it (V0.51.1): the picker is shared
 * with responsibilities, where the same shortcut would be a much heavier claim, and it has
 * never been told who the current user is.
 */
function TaskEditor({
  s,
  todo,
  onSave,
  onCancel,
}: {
  s: AppSnapshot;
  todo: Todo;
  onSave: (draft: Todos.TodoDraft) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(todo.title);
  const [notes, setNotes] = useState(todo.notes ?? '');
  const [due, setDue] = useState(todo.dueDate ?? '');
  const [userIds, setUserIds] = useState<string[]>([...todo.assigneeUserIds]);
  const [groupIds, setGroupIds] = useState<string[]>([...todo.assigneeGroupIds]);
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => firstRef.current?.focus(), []);

  const me = s.currentUser?.id ?? null;
  const commit = () =>
    onSave({
      title: title.trim() || todo.title,
      notes: notes.trim() ? notes : null,
      dueDate: due || null,
      // BOTH families, always — the write is total, so omitting one would clear it.
      assigneeUserIds: userIds,
      assigneeGroupIds: groupIds,
    });

  return (
    <div
      className="todoedit"
      data-todo-edit={todo.id}
      /* Enter saves, Escape cancels — and both stop here, or the popup's own handlers would
         save the node or close the whole card underneath this editor. The calendar swallows its
         own Escape first, so one press closes the grid and the next cancels the edit. */
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
          e.preventDefault();
          e.stopPropagation();
          commit();
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
      /* The editor sits inside a row's block; neither of the row's own gestures applies to it. */
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <input
        ref={firstRef}
        className="todoedit-title"
        value={title}
        placeholder={todoText('titlePlaceholder', s.currentLocale)}
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        className="todoedit-notes"
        value={notes}
        placeholder={todoText('notesPlaceholder', s.currentLocale)}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="todoedit-meta">
        <label className="todoedit-due">
          {todoText('due', s.currentLocale)}
          <DatePicker
            value={due || null}
            today={todayKey()}
            quick
            locale={s.currentLocale}
            onChange={(day) => setDue(day ?? '')}
          />
        </label>
        {me && !userIds.includes(me) ? (
          <button type="button" className="pbtn todo-assignme" onClick={() => setUserIds([...userIds, me])}>
            {todoText('assignMe', s.currentLocale)}
          </button>
        ) : null}
      </div>
      <SubjectPicker
        id={`todo-assignee-${todo.id}`}
        users={s.userDirectory}
        groups={s.groupDirectory}
        // Everybody is eligible — see the header.
        eligibleUser={() => true}
        eligibleGroup={() => true}
        chosenUsers={userIds}
        chosenGroups={groupIds}
        onChangeUsers={setUserIds}
        onChangeGroups={setGroupIds}
        emptyUsersText="No accounts to assign this to."
        emptyGroupsText="No teams to assign this to."
      />
      <div className="todoedit-foot">
        <button type="button" className="pbtn" onClick={onCancel}>
          {todoText('cancel', s.currentLocale)}
        </button>
        <button type="button" className="pbtn primary" onClick={commit}>
          {todoText('save', s.currentLocale)}
        </button>
      </div>
    </div>
  );
}

/** Re-exported for the "My items" pane, which draws the same due-date and notes rules. */
export { isOverdue, formatDue, hasNotes, initialsOf };
export type { MyTodo };
